import { RevCheckbox, RevTooltip } from "@revmatics/catalyst";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faUpRightAndDownLeftFromCenter,
} from "@fortawesome/pro-light-svg-icons";
import { fullFormatNumbers } from "../../../../../../../helpers/functions/formatNumber.ts";
import { getFitName } from "../../../../../../../helpers/functions/getFitName.ts";
import { CommunityDataItemType } from "../../../../../../../stores/types/discoverCommunitiesTypes.tsx";
import { useEffect, useRef, useState } from "react";

type Props = {
  community: CommunityDataItemType;
  selectedCommunity: Set<number>;
  setSelectedCommunity: (value: Set<number>) => void;
  selectedInterestsCommunity: Set<string>;
  selectedInterests: Set<string>;
};

export const CommunityCheckboxCard = ({
  community,
  selectedCommunity,
  setSelectedCommunity,
  selectedInterestsCommunity,
  selectedInterests,
}: Props) => {
  const communityId = community.community.id;

  const nameRef = useRef<HTMLHeadingElement>(null);
  const interestsRef = useRef<HTMLParagraphElement>(null);

  const [isOverflowed, setIsOverflowed] = useState(false);

  const getInterestsForCard = (communityInterests: string[]) => {
    let result = communityInterests;
    if (
      (selectedInterestsCommunity.size > 0 &&
        [...selectedInterestsCommunity].some((interest) =>
          communityInterests.includes(interest),
        )) ||
      (selectedInterests.size > 0 &&
        [...selectedInterests].some((interest) =>
          communityInterests.includes(interest),
        ))
    ) {
      result = communityInterests.filter(
        (interest) =>
          selectedInterestsCommunity.has(interest) ||
          selectedInterests.has(interest),
      );
    }
    return result.slice(0, 3).join(", ");
  };

  const interestsText = getInterestsForCard(community.community.interests);

  useEffect(() => {
    const nameEl = nameRef.current;
    const interestsEl = interestsRef.current;

    if (!nameEl || !interestsEl) return;

    const nameOverflow = nameEl.scrollWidth > nameEl.clientWidth;

    const interestsOverflow = interestsEl.scrollWidth > interestsEl.clientWidth;
    setIsOverflowed(nameOverflow || interestsOverflow);
  }, [community.community.name, interestsText]);

  const content = (
    <RevCheckbox
      checked={selectedCommunity.has(communityId)}
      className={`w-full bg-white p-3 rounded-lg ${selectedCommunity.has(communityId) ? "outline-2 outline-primary-500" : "outline-1 outline-monochrome-200 hover:outline-primary-500"}`}
      handleChecked={() => {
        const updatedSet = new Set(selectedCommunity);
        if (updatedSet.has(communityId)) {
          updatedSet.delete(communityId);
        } else {
          updatedSet.add(communityId);
        }
        setSelectedCommunity(updatedSet);
      }}
    >
      <div data-testid="community-card" className="space-y-0.5 overflow-hidden">
        <h6
          ref={nameRef}
          data-testid="community-name"
          className="text-start whitespace-nowrap overflow-hidden text-ellipsis "
        >
          {community.community.name}
        </h6>
        <div className="flex items-center gap-2 text-monochrome-600 typography-body-xs">
          <div className="flex items-center gap-1 ">
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
            <p>
              {fullFormatNumbers({
                value: community.community.size,
                shortenValue: 1e3,
              })}
            </p>
          </div>
          <span className="w-1 h-1 rounded-full bg-monochrome-600" />
          <div className="flex items-center gap-1">
            <FontAwesomeIcon icon={faLink} />
            <p className="capitalize">
              {getFitName(community.affiliation?.toLowerCase() || "")}
            </p>
          </div>
        </div>
        <p
          ref={interestsRef}
          className="typography-body-xs text-start text-monochrome-600 whitespace-nowrap overflow-hidden text-ellipsis "
        >
          {getInterestsForCard(community.community.interests)}
        </p>
      </div>
    </RevCheckbox>
  );

  if (isOverflowed) {
    return (
      <RevTooltip
        position="topLeft"
        content={
          <div className="space-y-1">
            <p className="typography-body-bold-sm text-start">
              {community.community.name}
            </p>
            <p className="typography-body-xs text-start">
              {getInterestsForCard(community.community.interests)}
            </p>
          </div>
        }
        className="w-[320px]"
      >
        <div>{content}</div>
      </RevTooltip>
    );
  }

  return content;
};
